import { useState, type ReactNode } from "react";
import { fMid } from "../lib/format";

/** What a text flyer needs: the bill (headliner first), the venue, the date. */
export interface FlyerText {
  date: string;
  bands: string[];
  venue: string;
}

/** A show with no flyer image: a quiet tile with the date, headliner and venue (Matt's pick, 2026-09-11). */
function TextFlyer({ text }: { text: FlyerText }) {
  const bands = text.bands.filter(Boolean);
  const head = bands[0] ?? "Show";
  return (
    <div className="flyer text" role="img" aria-label={`${head} at ${text.venue}, ${fMid(text.date)}`}>
      {/* One level in: cq units never resolve against the element that declares
          the container, so the padded layout cannot live on .flyer itself. */}
      <div className="ftx">
        <span className="fd">{fMid(text.date)}</span>
        <span className="fh">{head}</span>
        <span className="fv">{text.venue}</span>
      </div>
    </div>
  );
}

/**
 * A flyer, never cropped. With no image, or one that fails to load (a missing
 * object, like #1147's), it shows the text version when `text` is given.
 * `natural` drops the 4:5 slot and shows the image at its own shape.
 * `fallback` replaces both stand-ins, for slots too small to print the text.
 * `onZoom` makes the image itself a button that opens it full size; the text
 * and date stand-ins never get one, because there is nothing to enlarge.
 */
export function Flyer({ url, alt = "", text, natural = false, fallback, onZoom }: { url: string | null | undefined; alt?: string; text?: FlyerText; natural?: boolean; fallback?: ReactNode; onZoom?: () => void }) {
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  if (!url || brokenUrl === url) {
    if (fallback) return <>{fallback}</>;
    if (text) return <TextFlyer text={text} />;
    return (
      <div className="flyer none">
        <span>No flyer</span>
      </div>
    );
  }
  const shot = (
    <div className={`flyer${natural ? " natural" : ""}`}>
      <img src={url} alt={alt} loading="lazy" decoding="async" onError={() => setBrokenUrl(url)} />
    </div>
  );
  if (!onZoom) return shot;
  return (
    <button type="button" className="flyer-open" onClick={onZoom} aria-label={alt ? `${alt}, full size` : "See this flyer full size"}>
      {shot}
    </button>
  );
}
